import { useState } from 'react'
import { KeyRound, Smartphone } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Logo } from '@/components/Logo'
import { LanguageSwitcher } from '@/components/LanguageSwitcher'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { useAuthStore } from '@/app/store/auth'
import { handleApiError } from '@/lib/api/axios'
import { isWebAuthnSupported } from '@/lib/security/webauthn-enterprise'
import { enrolmentApi } from '../api'

/**
 * Sign-in, for a user with no sign-in device yet: the one-time enrolment code.
 *
 * The password was right, and that earns only this screen. A first device is set up on a code
 * somebody already trusted issued for this user - never on the password alone (Andrew, 13 Oct
 * 2026). A right code earns the token the set-up screens use; the code itself is spent when the
 * device is enrolled.
 */
export function EnrolmentCodeStep() {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const [code, setCode] = useState('')
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState<string | null>(null)
    // Twelve symbols, however they are spaced or cased: the server reads it the same way.
    const complete = code.replace(/[\s-]/g, '').length === 12
    // What the first device may be: what sign-in allowed, less a passkey this browser cannot make.
    // A user who was given one method has no choice to make; one given none chooses (Stage 3).
    const allowed = useAuthStore((state) => state.mfaMethods || [])
        .filter((m): m is 'totp' | 'webauthn' => m === 'totp' || (m === 'webauthn' && isWebAuthnSupported()))
    const [chosen, setChosen] = useState<'totp' | 'webauthn' | null>(null)
    const factor = chosen ?? allowed[0] ?? null

    const submit = async () => {
        const { enrolmentToken, mfaMethods, mfaToken, setMfaRequired } = useAuthStore.getState()
        if (!enrolmentToken || !complete || !factor) return
        setBusy(true)
        setError(null)
        try {
            const accepted = await enrolmentApi.redeem(enrolmentToken, code, factor)
            // The set-up screen for that kind of device takes it from here; this also clears the
            // enrolment token.
            setMfaRequired(true, mfaMethods, true, accepted.mfaSetupToken, mfaToken)
            useAuthStore.getState().setMfaEnrolFactor(accepted.factor)
        } catch (e) {
            setError(handleApiError(e))
        } finally {
            setBusy(false)
        }
    }
    const startAgain = () => {
        useAuthStore.getState().logout()
        navigate('/login')
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
            <div className="w-full max-w-md space-y-6">
                <div className="flex justify-end"><LanguageSwitcher /></div>
                <div className="flex justify-center"><Logo className="h-16" /></div>
                <Card data-testid="enrolment-code-step">
                    <CardHeader>
                        <CardTitle>{t('enrolment.step.title')}</CardTitle>
                        <CardDescription>{t('enrolment.step.description')}</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); submit() }}>
                            <div className="space-y-2">
                                <Label htmlFor="enrolment-code">{t('enrolment.step.codeLabel')}</Label>
                                <Input
                                    id="enrolment-code" value={code} autoFocus autoComplete="off" autoCapitalize="characters" spellCheck={false}
                                    maxLength={20} placeholder="XXXX-XXXX-XXXX" className="font-mono tracking-widest"
                                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                                />
                            </div>
                            {allowed.length > 1 && (
                                <div className="space-y-2" role="radiogroup" aria-label={t('signInDevice.chooseLabel')}>
                                    <Label>{t('signInDevice.chooseLabel')}</Label>
                                    <div className="grid grid-cols-2 gap-2">
                                        {allowed.map((kind) => (
                                            <Button
                                                key={kind} type="button" role="radio" aria-checked={factor === kind}
                                                variant={factor === kind ? 'default' : 'outline'} className="justify-start"
                                                onClick={() => setChosen(kind)}
                                            >
                                                {kind === 'webauthn' ? <KeyRound className="mr-2 h-4 w-4" /> : <Smartphone className="mr-2 h-4 w-4" />}
                                                {t(kind === 'webauthn' ? 'signInDevice.passkey' : 'signInDevice.totp')}
                                            </Button>
                                        ))}
                                    </div>
                                    <p className="text-xs text-muted-foreground">{t(factor === 'webauthn' ? 'signInDevice.passkeyHint' : 'signInDevice.totpHint')}</p>
                                </div>
                            )}
                            {allowed.length === 0 && <p className="text-sm text-destructive" role="alert">{t('signInDevice.noneHere')}</p>}
                            {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
                            <Button type="submit" className="w-full" disabled={busy || !complete || !factor}>{t('enrolment.step.continue')}</Button>
                            <p className="text-xs text-muted-foreground">{t('enrolment.step.noCode')}</p>
                            <Button type="button" variant="ghost" className="w-full" onClick={startAgain} disabled={busy}>{t('enrolment.step.back')}</Button>
                        </form>
                    </CardContent>
                </Card>
            </div>
        </div>
    )
}
